/**
 *
 * @module deviceManager
 * @note it is necessary to import deviceGrantStorage directly to avoid circular dependency
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import DeviceGrantStorage from './../token/deviceGrantStorage';

const EnvironmentConfiguration = Services.Configuration.EnvironmentConfiguration;
const DeviceManagerConfiguration = Services.Configuration.DeviceManagerConfiguration;
const DeviceClient = Services.Device.DeviceClient;
const DeviceMetadata = Services.Device.DeviceMetadata;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class DeviceManager
 * @extends {DeviceGrantProvider}
 *
 */
export default class DeviceManager {

    /**
     *
     * @param {String} apiKey
     * @param {SDK.Services.Configuration.DeviceManagerConfiguration} config
     * @param {SDK.Services.Device.DeviceClient} deviceClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.DeviceGrantStorage} deviceGrantStorage
     * @param {SDK.Services.Configuration.EnvironmentConfiguration} environmentConfiguration
     * @throws {InvalidArgumentException}
     *
     */
    constructor(apiKey, config, deviceClient, logger = Logging, deviceGrantStorage, environmentConfiguration) {

        const errorMessage = {
            ctor: 'DeviceManager(apiKey, config, deviceClient, logger, deviceGrantStorage, environmentConfiguration)',
            get apiKey() {
                return `${this.ctor} apiKey should be a String with length`;
            },
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.DeviceManagerConfiguration instance`;
            },
            get deviceClient() {
                return `${this.ctor} needs SDK.Services.Device.DeviceClient instance`;
            },
            get deviceGrantStorage() {
                return `${this.ctor} needs SDK.Token.DeviceGrantStorage instance`;
            },
            get environmentConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.EnvironmentConfiguration instance`;
            }
        };

        nonEmptyString(apiKey, errorMessage.apiKey, InvalidArgumentException);
        instanceStrict(config, DeviceManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(deviceClient, DeviceClient, errorMessage.deviceClient, InvalidArgumentException);
        instanceStrict(deviceGrantStorage, DeviceGrantStorage, errorMessage.deviceGrantStorage, InvalidArgumentException); // eslint-disable-line
        instanceStrict(environmentConfiguration, EnvironmentConfiguration, errorMessage.environmentConfiguration, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.apiKey = apiKey;

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.DeviceManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.Device.DeviceClient}
         *
         */
        this.client = deviceClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         * @memberof {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.DeviceGrantStorage}
         *
         */
        this.deviceGrantStorage = deviceGrantStorage;

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.EnvironmentConfiguration}
         *
         */
        this.environmentConfiguration = environmentConfiguration;

        this.logger.log(DeviceManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @returns {Promise<SDK.Services.Token.DeviceGrant>}
     *
     */
    getDeviceGrant() {

        const deviceGrant = this.deviceGrantStorage.getDeviceGrant();

        if (Check.assigned(deviceGrant)) {
            return Promise.resolve(deviceGrant);
        }

        return this.createDeviceGrant();

    }

    /**
     *
     * @access private
     * @desc Create a new device grant. It should only be called if there
     * is no stored device grant
     * @returns {Promise<SDK.Services.Token.DeviceGrant>}
     *
     */
    createDeviceGrant() {

        const { apiKey, client, deviceGrantStorage } = this;
        const { logger, environmentConfiguration } = this;
        const { deviceFamily, applicationRuntime, deviceProfile } = environmentConfiguration;

        const deviceMetadata = new DeviceMetadata(
            deviceFamily,
            applicationRuntime,
            deviceProfile
        );

        return client.createDeviceGrant(deviceMetadata, apiKey).then((deviceGrant) => {

            logger.info(DeviceManager.name, 'DeviceGrant Created');

            return deviceGrantStorage.saveDeviceGrant(deviceGrant).then(() => {
                return Promise.resolve(deviceGrant);
            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/device/deviceManager.js